import React, { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Alert from "../../components/common/Alert";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import { useGetCompletedOrdersQuery } from "../../store/api";
import { formatCurrency, formatDateTime } from "../../utils/reporting";

const DetailPair = ({ label, value, emphasize = false }) => (
  <div className="grid grid-cols-[7.5rem_auto] items-center gap-3 border-b border-slate-100 py-2.5 last:border-b-0 sm:grid-cols-[8.5rem_auto]">
    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
      {label}
    </p>
    <p
      className={[
        "text-slate-900",
        emphasize ? "text-base font-semibold" : "text-sm font-medium",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {value}
    </p>
  </div>
);

const CompletedOrderDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams();

  const decodedOrderId = decodeURIComponent(orderId || "");
  const orderFromState = location.state?.order || null;
  const shouldFetchFallback = !orderFromState && Boolean(decodedOrderId);

  const { data, isFetching, isError, error } = useGetCompletedOrdersQuery(
    shouldFetchFallback
      ? {
          search: decodedOrderId,
          page: 1,
          pageSize: 20,
        }
      : undefined,
    { skip: !shouldFetchFallback },
  );

  const orderFromQuery = useMemo(() => {
    const orders = Array.isArray(data?.orders) ? data.orders : [];
    if (orders.length === 0) {
      return null;
    }

    return (
      orders.find((order) => {
        const keys = [
          order?.edgeOrderId,
          order?.orderId,
          order?.orderNo,
          `${order?.storeId || ""}-${order?.orderNo || ""}`,
        ]
          .filter(Boolean)
          .map((value) => `${value}`);

        return keys.includes(decodedOrderId);
      }) || orders[0]
    );
  }, [data, decodedOrderId]);

  const order = orderFromState || orderFromQuery;
  const currencyCode = order?.currencyCode || "USD";
  const orderDateTime = formatDateTime(order?.completedAt || order?.placedAt);
  const orderMeta = [
    { label: "Store", value: order?.storeName || "-" },
    { label: "Channel", value: order?.channel || "-" },
    { label: "Order Type", value: order?.orderType || "-" },
    { label: "Payment Status", value: order?.paymentStatus || "-" },
    { label: "Date", value: orderDateTime || "-" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            Order Details {order?.orderNo ? `- ${order.orderNo}` : ""}
          </h1>
          <p className="text-xs text-slate-600">
            Detailed completed-order summary.
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => navigate("/reporting/completed-orders")}
        >
          Back
        </Button>
      </div>

      <Card className="p-5 sm:p-6">
        {isFetching && !order && (
          <div className="space-y-3">
            {[1, 2, 3].map((value) => (
              <div
                key={value}
                className="h-12 animate-pulse rounded-lg bg-slate-100"
              />
            ))}
          </div>
        )}

        {isError && (
          <Alert tone="error">
            {error?.data?.detail || "Failed to load order details."}
          </Alert>
        )}

        {!isFetching && !order && !isError && (
          <Alert tone="warning">
            Order details are not available. Open this page from Completed
            Orders and try again.
          </Alert>
        )}

        {order && (
          <div className="space-y-5">
            <div>
              <section className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                  {orderMeta.map((meta) => (
                    <div key={meta.label} className="min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        {meta.label}
                      </p>
                      <p className="truncate text-sm font-medium text-slate-900">
                        {meta.value}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <section className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="text-base font-semibold text-slate-900">
                Order Totals
              </h3>
              <div className="mt-2 grid gap-x-6 sm:grid-cols-2 xl:grid-cols-3">
                <DetailPair
                  label="Subtotal"
                  value={formatCurrency(order.subtotal, currencyCode)}
                />
                <DetailPair
                  label="Tax"
                  value={formatCurrency(order.taxTotal, currencyCode)}
                />
                <DetailPair
                  label="Paid Amount"
                  value={formatCurrency(order.paidAmount, currencyCode)}
                />
                <DetailPair
                  label="Grand Total"
                  value={formatCurrency(order.grandTotal, currencyCode)}
                  emphasize
                />
              </div>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="text-base font-semibold text-slate-900">Items</h3>
              {Array.isArray(order.items) && order.items.length > 0 ? (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-2 py-2">Item</th>
                        <th className="px-2 py-2 text-right">Qty</th>
                        <th className="px-2 py-2 text-right">Unit Price</th>
                        <th className="px-2 py-2 text-right">Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item, index) => (
                        <tr
                          key={item?.itemId || `${item?.itemName}-${index}`}
                          className="border-b border-slate-100 last:border-b-0"
                        >
                          <td className="px-2 py-2">
                            <div className="font-medium text-slate-900">
                              {item?.itemName || "-"}
                            </div>
                          </td>
                          <td className="px-2 py-2 text-right text-slate-700">
                            {item?.quantity ?? "-"}
                          </td>
                          <td className="px-2 py-2 text-right text-slate-700">
                            {formatCurrency(item?.unitPrice, currencyCode)}
                          </td>
                          <td className="px-2 py-2 text-right font-semibold text-slate-900">
                            {formatCurrency(item?.lineTotal, currencyCode)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-2 text-sm text-slate-500">
                  No items available for this order.
                </p>
              )}
            </section>

          </div>
        )}
      </Card>
    </div>
  );
};

export default CompletedOrderDetail;
